import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  ArrowLeft,
  Users,
  Plus,
  Trash2,
  User,
  Loader2,
  Shield,
  UserCircle,
  Phone,
  Mail,
  MapPin,
  Crown,
  Edit,
  ShieldOff,
  Building2,
  Bell,
  UserPlus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { CollaboratorRequestsList } from "../components/CollaboratorRequests";

const MACRO_AREE = [
  { value: "ORTOPEDIA", label: "Ortopedia", color: "bg-blue-500" },
  { value: "FINANZIARIO", label: "Finanziario", color: "bg-green-500" },
  { value: "CAF", label: "CAF", color: "bg-purple-500" },
  { value: "PATRONATO", label: "Patronato", color: "bg-orange-500" },
  { value: "LEGALE", label: "Legale", color: "bg-red-500" },
  { value: "ASSICURAZIONE", label: "Assicurazione", color: "bg-teal-500" },
  { value: "LUCE_E_GAS", label: "Luce e Gas", color: "bg-yellow-500" }
];

const Collaborators = ({ user }) => {
  const navigate = useNavigate();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCollab, setEditingCollab] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [showRequests, setShowRequests] = useState(false);
  const [newCollab, setNewCollab] = useState({ 
    username: "", 
    password: "", 
    name: "", 
    telefono: "", 
    email: "", 
    indirizzo: "",
    role: "collaboratore",
    macro_area: "",  // For titolare
    macro_aree_visibili: []  // For collaboratore
  });
  
  const fetchPendingRequestsCount = async () => {
    try {
      const response = await axios.get(`${API}/collaborator-requests/pending-count`, { withCredentials: true });
      setPendingRequestsCount(response.data.count);
    } catch (error) {
      console.error("Error fetching pending requests count:", error);
    }
  };
  
  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Accesso non autorizzato");
      navigate("/dashboard");
      return;
    }
    fetchCollaborators();
    fetchPendingRequestsCount();
  }, [user, navigate]);
  
  const fetchCollaborators = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/collaborators`, { withCredentials: true });
      setCollaborators(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento collaboratori");
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = async () => {
    if (!newCollab.username || !newCollab.password || !newCollab.name || !newCollab.email) {
      toast.error("Compila tutti i campi obbligatori (nome, username, password, email)");
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCollab.email)) {
      toast.error("Inserisci un indirizzo email valido");
      return;
    }
    
    if (newCollab.password.length < 4) {
      toast.error("La password deve avere almeno 4 caratteri");
      return;
    }
    
    // Validate macro area for titolare
    if (newCollab.role === "titolare" && !newCollab.macro_area) {
      toast.error("Seleziona una macro area per il titolare");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/collaborators`, newCollab, { withCredentials: true });
      toast.success(newCollab.role === "titolare" ? "Titolare creato" : "Collaboratore creato");
      setDialogOpen(false);
      setNewCollab({ username: "", password: "", name: "", telefono: "", email: "", indirizzo: "", role: "collaboratore", macro_area: "", macro_aree_visibili: [] });
      fetchCollaborators();
    } catch (error) {
      if (error.response?.data?.detail?.includes("Username già")) {
        toast.error("Username già in uso");
      } else {
        toast.error("Errore nella creazione");
      }
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (collabId) => {
    if (!confirm("Eliminare questo collaboratore?")) return;
    
    try {
      await axios.delete(`${API}/collaborators/${collabId}`, { withCredentials: true });
      toast.success("Collaboratore eliminato");
      fetchCollaborators();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  const handleReset2FA = async (collab) => {
    if (!confirm(`Resettare il 2FA per ${collab.name}? L'utente dovrà riconfigurare Google Authenticator al prossimo accesso.`)) return;
    
    try {
      const response = await axios.post(`${API}/collaborators/${collab.user_id}/reset-2fa`, {}, { withCredentials: true });
      toast.success(response.data.message || "2FA resettato con successo");
      fetchCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset del 2FA");
    }
  };
  
  const handleEdit = (collab) => {
    setEditingCollab({
      user_id: collab.user_id,
      name: collab.name || "",
      email: collab.email || "",
      telefono: collab.telefono || "",
      indirizzo: collab.indirizzo || "",
      role: collab.role || "collaboratore",
      macro_area: collab.macro_area || "",
      macro_aree_visibili: collab.macro_aree_visibili || [],
      newPassword: ""  // Campo per nuova password
    });
    setEditDialogOpen(true);
  };
  
  const handleUpdate = async () => {
    if (!editingCollab?.name || !editingCollab?.email) {
      toast.error("Nome e email sono obbligatori");
      return;
    }
    
    // Validate macro area for titolare
    if (editingCollab.role === "titolare" && !editingCollab.macro_area) {
      toast.error("Seleziona una macro area per il titolare");
      return;
    }
    
    // Validate password if provided
    if (editingCollab.newPassword && editingCollab.newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    
    setSaving(true);
    try {
      const updateData = {
        name: editingCollab.name,
        email: editingCollab.email,
        telefono: editingCollab.telefono,
        indirizzo: editingCollab.indirizzo,
        role: editingCollab.role,
        macro_area: editingCollab.macro_area,
        macro_aree_visibili: editingCollab.macro_aree_visibili
      };
      
      // Include password only if provided
      if (editingCollab.newPassword) {
        updateData.password = editingCollab.newPassword;
      }
      
      await axios.put(`${API}/collaborators/${editingCollab.user_id}`, updateData, { withCredentials: true });
      
      if (editingCollab.newPassword) {
        toast.success("Collaboratore aggiornato e password modificata");
      } else {
        toast.success("Collaboratore aggiornato");
      }
      setEditDialogOpen(false);
      setEditingCollab(null);
      fetchCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSaving(true);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="collaborators-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Gestione Collaboratori
                </h1>
                <p className="text-xs text-slate-500">Aggiungi o rimuovi collaboratori</p>
              </div>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-collab-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Utente
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Aggiungi Utente</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="role">Ruolo *</Label>
                    <Select 
                      value={newCollab.role} 
                      onValueChange={(value) => setNewCollab(prev => ({ ...prev, role: value }))}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="collab-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="collaboratore">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Collaboratore
                          </div>
                        </SelectItem>
                        <SelectItem value="titolare">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-500" />
                            Titolare
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      {newCollab.role === "titolare" 
                        ? "Il titolare vede tutti gli ordini della sua macro area"
                        : "Il collaboratore vede solo le macro aree assegnate"
                      }
                    </p>
                  </div>
                  
                  {/* Macro Area Selection */}
                  {newCollab.role === "titolare" ? (
                    <div>
                      <Label>Macro Area Assegnata *</Label>
                      <Select 
                        value={newCollab.macro_area} 
                        onValueChange={(value) => setNewCollab(prev => ({ ...prev, macro_area: value }))}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="collab-macro-area-select">
                          <SelectValue placeholder="Seleziona macro area..." />
                        </SelectTrigger>
                        <SelectContent>
                          {MACRO_AREE.map((area) => (
                            <SelectItem key={area.value} value={area.value}>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${area.color}`}></div>
                                {area.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label>Macro Aree Visibili</Label>
                      <p className="text-xs text-slate-500 mb-2">Seleziona le aree che il collaboratore può vedere</p>
                      <div className="grid grid-cols-2 gap-2 mt-1.5">
                        {MACRO_AREE.map((area) => (
                          <label 
                            key={area.value}
                            className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                          >
                            <Checkbox
                              checked={newCollab.macro_aree_visibili?.includes(area.value)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setNewCollab(prev => ({
                                    ...prev,
                                    macro_aree_visibili: [...(prev.macro_aree_visibili || []), area.value]
                                  }));
                                } else {
                                  setNewCollab(prev => ({
                                    ...prev,
                                    macro_aree_visibili: (prev.macro_aree_visibili || []).filter(v => v !== area.value)
                                  }));
                                }
                              }}
                            />
                            <div className={`w-2 h-2 rounded-full ${area.color}`}></div>
                            <span className="text-sm">{area.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="name">Nome Completo *</Label>
                    <Input
                      id="name"
                      value={newCollab.name}
                      onChange={(e) => setNewCollab(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Mario Rossi"
                      className="mt-1.5"
                      data-testid="collab-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      value={newCollab.username}
                      onChange={(e) => setNewCollab(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="mario.rossi"
                      className="mt-1.5"
                      data-testid="collab-username-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={newCollab.password}
                      onChange={(e) => setNewCollab(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      className="mt-1.5"
                      data-testid="collab-password-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="telefono">Telefono</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      value={newCollab.telefono}
                      onChange={(e) => setNewCollab(prev => ({ ...prev, telefono: e.target.value }))}
                      placeholder="+39 333 1234567"
                      className="mt-1.5"
                      data-testid="collab-telefono-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newCollab.email}
                      onChange={(e) => setNewCollab(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="mario.rossi@email.com"
                      className="mt-1.5"
                      required
                      data-testid="collab-email-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Email obbligatoria per ricevere le notifiche
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="indirizzo">Indirizzo</Label>
                    <Input
                      id="indirizzo"
                      value={newCollab.indirizzo}
                      onChange={(e) => setNewCollab(prev => ({ ...prev, indirizzo: e.target.value }))}
                      placeholder="Via Roma 1, 00100 Roma"
                      className="mt-1.5"
                      data-testid="collab-indirizzo-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Il collaboratore userà queste credenziali per accedere
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>
                      Annulla
                    </Button>
                    <Button
                      onClick={handleCreate}
                      disabled={saving}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="save-collab-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creazione...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Aggiungi
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Info Card */}
        <div className="card-hover p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Amministratore</p>
              <p className="font-semibold text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
        </div>
        
        {/* Pending Collaborator Requests Section */}
        {pendingRequestsCount > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setShowRequests(!showRequests)}
              className="w-full card-hover p-4 flex items-center justify-between hover:border-purple-300 transition-colors"
              data-testid="pending-requests-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white relative">
                  <UserPlus className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {pendingRequestsCount}
                  </span>
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-slate-900">Segnalazioni Nuovi Collaboratori</h3>
                  <p className="text-sm text-slate-500">
                    {pendingRequestsCount} {pendingRequestsCount === 1 ? 'richiesta' : 'richieste'} in attesa
                  </p>
                </div>
              </div>
              <Bell className={`w-5 h-5 text-purple-500 ${pendingRequestsCount > 0 ? 'animate-bounce' : ''}`} />
            </button>
            
            {showRequests && (
              <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                <h4 className="font-medium text-purple-800 dark:text-purple-300 mb-4">
                  Richieste dai Titolari
                </h4>
                <CollaboratorRequestsList 
                  isAdmin={true} 
                  onUpdate={fetchPendingRequestsCount}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Collaborators List */}
        <div className="card-hover overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Collaboratori ({collaborators.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
              ))}
            </div>
          ) : collaborators.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Nessun collaboratore</p>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(true)}
                data-testid="empty-add-collab-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi il primo collaboratore
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {collaborators.map((collab, index) => (
                <div
                  key={collab.user_id}
                  className="p-4 sm:p-6 hover:bg-slate-50 transition-colors flex items-center gap-4 animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`collab-row-${collab.user_id}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    collab.role === "titolare" ? "bg-amber-50" : "bg-slate-100"
                  }`}>
                    {collab.picture ? (
                      <img src={collab.picture} alt="" className="w-12 h-12 rounded-full" />
                    ) : collab.role === "titolare" ? (
                      <Crown className="w-6 h-6 text-amber-500" />
                    ) : (
                      <UserCircle className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{collab.name}</span>
                      <Badge variant="outline" className={`text-xs ${
                        collab.role === "titolare" 
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"
                      }`}>
                        {collab.role === "titolare" ? "Titolare" : "Collaboratore"}
                      </Badge>
                      {/* Show Macro Area Badge */}
                      {collab.role === "titolare" && collab.macro_area && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          <Building2 className="w-3 h-3 mr-1" />
                          {collab.macro_area}
                        </Badge>
                      )}
                      {collab.role === "collaboratore" && collab.macro_aree_visibili?.length > 0 && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          {collab.macro_aree_visibili.length} aree
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        @{collab.username}
                      </span>
                      {collab.telefono && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {collab.telefono}
                        </span>
                      )}
                      {collab.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {collab.email}
                        </span>
                      )}
                      {collab.indirizzo && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {collab.indirizzo}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(collab)}
                      title="Modifica"
                      data-testid={`edit-collab-${collab.user_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-orange-600 hover:bg-orange-50"
                      onClick={() => handleReset2FA(collab)}
                      title="Reset 2FA"
                      data-testid={`reset-2fa-${collab.user_id}`}
                    >
                      <ShieldOff className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(collab.user_id)}
                      title="Elimina"
                      data-testid={`delete-collab-${collab.user_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Collaboratore</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="edit-name">Nome Completo *</Label>
              <Input
                id="edit-name"
                value={editingCollab?.name || ""}
                onChange={(e) => setEditingCollab(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Mario Rossi"
                className="mt-1.5"
                data-testid="edit-collab-name"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={editingCollab?.email || ""}
                onChange={(e) => setEditingCollab(prev => ({ ...prev, email: e.target.value }))}
                placeholder="mario.rossi@email.com"
                className="mt-1.5"
                data-testid="edit-collab-email"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-telefono">Telefono</Label>
              <Input
                id="edit-telefono"
                value={editingCollab?.telefono || ""}
                onChange={(e) => setEditingCollab(prev => ({ ...prev, telefono: e.target.value }))}
                placeholder="+39 333 1234567"
                className="mt-1.5"
                data-testid="edit-collab-phone"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-indirizzo">Indirizzo</Label>
              <Input
                id="edit-indirizzo"
                value={editingCollab?.indirizzo || ""}
                onChange={(e) => setEditingCollab(prev => ({ ...prev, indirizzo: e.target.value }))}
                placeholder="Via Roma 1, Milano"
                className="mt-1.5"
                data-testid="edit-collab-address"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-password">Nuova Password</Label>
              <Input
                id="edit-password"
                type="password"
                value={editingCollab?.newPassword || ""}
                onChange={(e) => setEditingCollab(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Lascia vuoto per non modificare"
                className="mt-1.5"
                data-testid="edit-collab-password"
              />
              <p className="text-xs text-slate-500 mt-1">Minimo 6 caratteri. Lascia vuoto per mantenere la password attuale.</p>
            </div>
            
            <div>
              <Label htmlFor="edit-role">Ruolo</Label>
              <Select 
                value={editingCollab?.role || "collaboratore"} 
                onValueChange={(value) => setEditingCollab(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="edit-collab-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="collaboratore">Collaboratore</SelectItem>
                  <SelectItem value="titolare">Titolare</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Macro Area Selection in Edit Dialog */}
            {editingCollab?.role === "titolare" ? (
              <div>
                <Label>Macro Area Assegnata *</Label>
                <Select 
                  value={editingCollab?.macro_area || ""} 
                  onValueChange={(value) => setEditingCollab(prev => ({ ...prev, macro_area: value }))}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Seleziona macro area..." />
                  </SelectTrigger>
                  <SelectContent>
                    {MACRO_AREE.map((area) => (
                      <SelectItem key={area.value} value={area.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${area.color}`}></div>
                          {area.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Macro Aree Visibili</Label>
                <p className="text-xs text-slate-500 mb-2">Seleziona le aree che il collaboratore può vedere</p>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {MACRO_AREE.map((area) => (
                    <label 
                      key={area.value}
                      className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={editingCollab?.macro_aree_visibili?.includes(area.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEditingCollab(prev => ({
                              ...prev,
                              macro_aree_visibili: [...(prev.macro_aree_visibili || []), area.value]
                            }));
                          } else {
                            setEditingCollab(prev => ({
                              ...prev,
                              macro_aree_visibili: (prev.macro_aree_visibili || []).filter(v => v !== area.value)
                            }));
                          }
                        }}
                      />
                      <div className={`w-2 h-2 rounded-full ${area.color}`}></div>
                      <span className="text-sm">{area.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="confirm-edit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  "Salva Modifiche"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Collaborators;
